import React, {useContext} from 'react';
import css from '../css/VerticalNavigation.module.css'
import NavigationLink from "./NavigationLink";
import {RxDashboard} from "react-icons/rx";
import {IoCardOutline, IoChatbubblesSharp, IoPerson, IoPersonOutline, IoSettingsOutline} from "react-icons/io5";
import useCompanyId from "../../../Hooks/useCompanyId";
import useImpersonation from "../../../Hooks/useImpersonation";
import {useAuth0} from "@auth0/auth0-react";
import {PiPolygonLight} from "react-icons/pi";
import {FaPersonDigging} from "react-icons/fa6";
import {BsBuilding, BsCalendar2Event} from "react-icons/bs";
import {Button, OverlayTrigger, Tooltip} from "react-bootstrap";
import ConditionalRender from "../../ConditionalRender";
import {MdLogout} from "react-icons/md";
import {useNavigate} from "react-router-dom";

import {SalvoContext} from "../../../contexts/SalvoContext";
import ClaimGuard from "../../Auth/ClaimGuard";
import {
    CREATE_USERS,
    GET_ALL_COMPANIES,
    GET_BILLING,
    GET_GEOFENCE,
    GET_HOURS,
    GET_MSISDN,
    GET_OVERVIEW,
    GET_SETTINGS,
    GET_TIMELINE,
    GET_USERS,
    SET_GLOBAL_SETTINGS,
    UPDATE_BILLING_ALL
} from "../../../Claims";
import {useTranslation} from "react-i18next";
import LanguageSelector from "./LanguageSelector";
import logo from '../../../assets/salvo-low.png'
import {TbTimelineEventPlus} from "react-icons/tb";
import {useHover} from "../../../Hooks/visual/useHover";
import useBalance from "../../../Hooks/useBalance";
import useClaims from "../../../Hooks/useClaims";

const VerticalNavigation = () => {
    const companyId = useCompanyId();
    const { logout, user } = useAuth0();

    const { hasClaim } = useClaims();

    const canFetchBalance =
        hasClaim(GET_BILLING) ||
        hasClaim(UPDATE_BILLING_ALL);

    const {
        balance,
        loading: balanceLoading
    } = useBalance(canFetchBalance);

    const { canImpersonate, isImpersonating } = useImpersonation();

    const {
        billingInfo,
        trialInfo,
        setSelectedCompany
    } = useContext(SalvoContext);
    const {t} = useTranslation()
    const navigate = useNavigate();

    const { style: balanceStyle, onMouseEnter: balanceOnMouseEnter, onMouseLeave: balanceOnMouseLeave } = useHover({
        textDecoration: 'underline',
        cursor: 'pointer',
    })

    const roles = user["https://salvo.nl/roles"];

    return (
        <div className={css.wrapper}>
            <div className={css.links}>
                <div className={'d-flex justify-content-center mt-2'}>
                    <img src={logo} alt={"logo"} className={'w-75 user-select-none mb-2'} style={{pointerEvents: 'none'}}/>
                </div>
                {/*<div className={'my-2'} style={{height: '2px', backgroundColor: 'white'}}/>*/}
                <ConditionalRender condition={!canImpersonate || isImpersonating}>
                    <ClaimGuard requiredClaim={GET_OVERVIEW}>
                        <NavigationLink text={t('navigation.dashboard')} Icon={RxDashboard} to='/dashboard'/>
                        <hr/>
                    </ClaimGuard>
                    <ClaimGuard requiredClaim={GET_MSISDN}>
                        <NavigationLink text={t('navigation.workers')} Icon={FaPersonDigging} to='/msisdns'/>
                    </ClaimGuard>
                    <ClaimGuard requiredClaim={GET_GEOFENCE}>
                        <NavigationLink text={t('navigation.workLocations')} Icon={PiPolygonLight} to='/geofences' activeLinks={['/workorders']}/>
                    </ClaimGuard>
                    {/*<NavigationLink text={t('navigation.workOrders')} Icon={GrBook} to='/workorders'/>*/}
                    <ClaimGuard requiredClaim={GET_HOURS}>
                        <hr/>
                        {/* <NavigationLink text={t('navigation.dailyReport')} Icon={BsCalendarDay} to='/hours' queryParams={{periodSelector: 'range'}} strict strictParams/> */}
                        <NavigationLink text={t('navigation.weeklyReport')} Icon={BsCalendar2Event} to='/hours' queryParams={{periodSelector: 'week'}} strict strictParams/>
                    </ClaimGuard>
                    <hr/>
                    <ClaimGuard requiredClaim={GET_TIMELINE}>
                        <NavigationLink text={t('navigation.timelineEvents')} Icon={TbTimelineEventPlus} to='/timeline/events'/>
                    </ClaimGuard>
                    <ClaimGuard anyOf={[GET_USERS, CREATE_USERS]}>
                        <hr/>
                        <NavigationLink text={t('navigation.users')} Icon={IoPersonOutline} to={`/users/${companyId}`}/>
                    </ClaimGuard>
                    <ClaimGuard requiredClaim={GET_SETTINGS}>
                        <NavigationLink text={t('navigation.settings')} Icon={IoSettingsOutline} to={`/settings`}/>
                    </ClaimGuard>
                    <ClaimGuard anyOf={[GET_BILLING, UPDATE_BILLING_ALL]}>
                        <ConditionalRender condition={billingInfo?.billingEnabled || trialInfo || hasClaim(UPDATE_BILLING_ALL)}>
                            <NavigationLink
                                text={t('navigation.creditManagement')}
                                Icon={IoCardOutline}
                                to={trialInfo ? `/trial` : `/billing`}
                            />
                        </ConditionalRender>
                    </ClaimGuard>
                </ConditionalRender>
                <ConditionalRender condition={canImpersonate && !isImpersonating}>
                    <ClaimGuard requiredClaim={GET_ALL_COMPANIES}>
                        <NavigationLink text={t('navigation.companies')} Icon={BsBuilding} to={`/companies`}/>
                    </ClaimGuard>
                    <ClaimGuard requiredClaim={SET_GLOBAL_SETTINGS}>
                        <NavigationLink text={t('navigation.settings')} Icon={IoSettingsOutline} to={`/settings`}/>
                    </ClaimGuard>

                </ConditionalRender>
            </div>
            <div>
                <ConditionalRender condition={canFetchBalance && !balanceLoading && balance != null && (trialInfo || billingInfo?.billingEnabled)}>
                    <span className="mb-2 d-block text-light text-center fw-bold"
                          onMouseEnter={balanceOnMouseEnter}
                          onMouseLeave={balanceOnMouseLeave}
                          style={balanceStyle}
                          onClick={() => navigate(trialInfo ? '/trial' : '/billing')}>
                        {t("creditManagement.balance")} {balance ?? ''}
                    </span>
                </ConditionalRender>

                <OverlayTrigger delay={{show: 400}} overlay={<Tooltip style={{ "--bs-tooltip-max-width": "480px" }}>{user.email} ({roles.join(", ")})</Tooltip>}>
                    <div
                        className={'border border-light rounded-2 mb-2 p-2 d-flex align-items-center text-light fw-semibold'}>
                        <IoPerson color={'white'} className={'flex-shrink-0'}/>
                        <span className={'ms-1 text-truncate'}>{user.email} ({roles.join(", ")})</span>
                    </div>
                </OverlayTrigger>

                <Button href={'https://forms.gle/HQ6CXwQasyWeyPR4A'}
                        variant="outline-light"
                        target='_blank'
                        className={'mb-2 w-100'}>
                    <IoChatbubblesSharp/>
                    <span className={'ms-1'}>{t('navigation.feedback')}</span>
                </Button>
                <div className='d-flex justify-content-between'>
                    <LanguageSelector/>
                    <Button variant={"danger"} className={'d-flex align-items-center w-100 ms-2'} onClick={async () => {
                        await logout({ logoutParams: { returnTo: window.location.origin } });
                        setSelectedCompany(null);
                    }}><MdLogout className='pe-1'/> {t('navigation.logout')}</Button>
                </div>
            </div>

        </div>
    );
};

export default VerticalNavigation;